/**
 * Whose move it is on an objective, and where a notification about it should land.
 *
 * ── Why this exists ───────────────────────────────────────────────────────────────────────
 *
 * The answer to "can this be submitted" was written out three separate times, and every one of
 * them said `status === DRAFT`:
 *
 *   EmployeeDashboard      decided whether to render the Submit bar at all
 *   handleSubmitAll        decided which objectives the bar actually submits
 *   handleEdit             decided whether an edit puts the objective back in the queue
 *
 * RETURNED is not DRAFT, so a returned objective failed all three. The bar did not appear, it
 * would not have submitted the objective if it had, and editing left the status where it was —
 * three independent ways of reaching the same dead end. A manager returned an objective, the
 * employee read the comment, fixed it, and then had nowhere to send it. It could never be
 * approved and could never be withdrawn; it was simply stuck.
 *
 * One function now answers it, and the three callers ask.
 *
 * ── The state machine ─────────────────────────────────────────────────────────────────────
 *
 *   DRAFT      ─submit──▶ SUBMITTED ─approve──▶ APPROVED ─┬─▶ IN_PROGRESS ─▶ UNDER_REVIEW
 *                  ▲           │                          └─ (employee edits ⇒ SUBMITTED)
 *                  │           └─return───▶ RETURNED
 *                  └──────── resubmit ────────┘
 *
 * RETURNED and DRAFT are the same thing from the employee's side — theirs to change and theirs
 * to send — and they are kept as separate statuses only because a return carries a manager
 * comment and a history a draft does not have. Treating them as one WHERE IT MATTERS, and only
 * there, is what unsticks the flow without losing that distinction on screen.
 *
 * APPROVED and IN_PROGRESS are editable too, and an edit to either re-opens approval: the
 * manager signed off on different words, so their approval no longer describes what is there.
 * That behaviour predates this file and is preserved exactly.
 */

import { Objective, ObjectiveStatus } from '../types/pms';

/**
 * Statuses the EMPLOYEE owns: theirs to edit, and theirs to submit.
 *
 * RETURNED sits here beside DRAFT, which is the whole point of this module.
 */
const EMPLOYEE_OWNED: ObjectiveStatus[] = [ObjectiveStatus.DRAFT, ObjectiveStatus.RETURNED];

/**
 * Statuses where an edit puts the objective back in front of the manager.
 *
 * Not the same list. A DRAFT edit is just a draft edit — nobody has seen it yet, so there is
 * nothing to re-open. An edit to an APPROVED or IN_PROGRESS objective invalidates an approval
 * that was given for different content. RETURNED is here because the employee editing it is
 * precisely the act of responding to the return.
 */
const REOPENS_APPROVAL: ObjectiveStatus[] = [
  ObjectiveStatus.APPROVED,
  ObjectiveStatus.IN_PROGRESS,
  ObjectiveStatus.RETURNED,
];

/** Whether the employee may still change this objective and send it for approval. */
export const isEmployeeTurn = (status?: ObjectiveStatus | string): boolean =>
  EMPLOYEE_OWNED.includes(status as ObjectiveStatus);

/** Whether this objective is waiting on a manager's decision — the Approve/Return state. */
export const isManagerTurn = (status?: ObjectiveStatus | string): boolean =>
  status === ObjectiveStatus.SUBMITTED;

/**
 * Whether saving an edit to this objective should send it back for approval.
 *
 * Read BEFORE the edit is applied, from the status the objective had when it was opened.
 */
export const editReopensApproval = (status?: ObjectiveStatus | string): boolean =>
  REOPENS_APPROVAL.includes(status as ObjectiveStatus);

/** The objectives in a list that the employee can submit right now. */
export const submittableObjectives = (objectives: Objective[]): Objective[] =>
  objectives.filter(objective => isEmployeeTurn(objective.status));

/**
 * Whether a submission is a RESUBMISSION — a second look, after a return.
 *
 * Determined from the status being left, not from a counter, because the status is the thing
 * that already records it. The manager is told which of the two this is, since "resubmitted"
 * tells them to look for changes against comments they wrote, and "submitted" does not.
 */
export const isResubmission = (previousStatus?: ObjectiveStatus | string): boolean =>
  previousStatus === ObjectiveStatus.RETURNED;

/**
 * Where a notification about this objective should take its recipient.
 *
 * ── Why the link and not the sidebar ──────────────────────────────────────────────────────
 *
 * Every PMS notification pointed at `/performance/objectives/{id}`, whoever it was for. The
 * sidebar marks an entry active when the path starts with it, so a manager opening "Usha
 * submitted an objective for your approval" landed on a URL beginning `/performance/objectives/`
 * and watched MY OBJECTIVES light up while they reviewed somebody else's goal.
 *
 * Forcing the sidebar to guess would be the wrong repair: it is reading the path correctly, and
 * the path was the thing that did not say where the reader was. So the LINK carries the context
 * instead, which also makes a refresh and a pasted URL behave the same as the click — the
 * destination is in the address, not in some state the notification handler set on its way past.
 *
 *   employee, about their own objective   /performance/objectives/{id}      My Objectives
 *   manager, about somebody else's        /performance/approvals?objective={id}   Pending Approvals
 */
export const objectiveLinkForEmployee = (objectiveId: string): string =>
  `/performance/objectives/${objectiveId}`;

export const objectiveLinkForApprover = (objectiveId: string): string =>
  `/performance/approvals?objective=${encodeURIComponent(objectiveId)}`;
